<template>
  <LoadingBar v-if="loading" />
  <Page v-else :title="pageTitle">
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      @submit.prevent="saveCustomCsv"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model="name"
            :rules="rules.name"
            label="Download Name"
            @keydown.enter.prevent
            @blur="onNameBlur"
          ></v-text-field>
          <v-textarea
            v-model="description"
            :rules="rules.description"
            label="Download Description"
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row v-if="user.is_copy_to && !id">
        <v-col cols="12" sm="10">
          <v-autocomplete
            :items="clients"
            label="Clients"
            no-data-text="No clients to select from"
            item-text="title"
            item-value="id"
            v-model="client_ids"
            clearable
            @click:clear="clearClients()"
            multiple
            chips
            deletable-chips
            :rules="rules.atLeastOne"
          >
            <template v-slot:label>
              <h4>
                Clients
                <span style="color: red;">
                  *
                </span>
              </h4>
            </template></v-autocomplete
          >
        </v-col>
        <v-col>
          <v-checkbox
            :input-value="isAllClientsSelected"
            :indeterminate="isSomeClientsSelected"
            label="Select All Clients?"
            @change="toggleSelectAll"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header
                >Components to Include in Download</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-row>
                  <v-col
                    cols="6"
                    class="flex-column"
                    @drop="onDrop($event, true)"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <v-list dense>
                      <v-subheader>Report Components</v-subheader>
                      <v-list-item
                        :key="i"
                        v-for="(item, i) in selectedComponents"
                        @drop="onDrop($event, true, i)"
                        @dragover.prevent
                        @dragenter="dragEnter($event, item)"
                        @dragleave="dragLeave($event, item)"
                        draggable="true"
                        @dragstart="startDrag($event, item)"
                      >
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="compName(item.id)"
                          ></v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col
                    cols="6"
                    class="flex-column"
                    @drop="onDrop($event, false)"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <v-list dense>
                      <v-subheader>Available Components</v-subheader>
                      <v-list-item
                        :key="i"
                        v-for="(item, i) in availableComponents"
                        @drop="onDrop($event, false)"
                        @dragover.prevent
                        @dragenter.prevent
                        draggable="true"
                        @dragstart="startDrag($event, item)"
                      >
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="compName(item.id)"
                          ></v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row class="pa-5">
        <v-btn @click="cancel()" color="secondary">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="saveCustomCsv"
          :disabled="saveLoading"
          type="submit"
        >
          <template v-if="saveLoading">
            <v-progress-circular
              indeterminate
              size="20"
              width="3"
              color="white"
            />
          </template>

          <template v-else>
            Save
          </template>
        </v-btn>
      </v-row>
    </v-form>
  </Page>
</template>

<script>
import Page from "@/components/layout/Page";
import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import LoadingBar from "@/components/loaders/LoadingBar";
import rules from "@/utils/rules";
import { polyfill } from "mobile-drag-drop";

polyfill({});

export default {
  name: "EditCsvDownloads",
  components: { LoadingBar, Page },
  data() {
    return {
      loading: true,
      valid: false,
      saveLoading: false,
      selectAllClients: false,
      allUserClientIds: [],
      selectedIds: [],
      rules: {
        name: [
          rules.required,
          rules.maxLength(255),
          rules.trim("download name")
        ],
        description: [rules.maxLength(255)],
        required: [rules.required],
        atLeastOne: [rules.atLeastOne]
      }
    };
  },
  computed: {
    ...mapState("csvs", ["detailItem"]),
    ...mapState("auth", ["user"]),
    ...mapState("baseData", ["test_components"]),
    ...mapState("clients", { clients: "items" }),
    ...mapGetters("clients", ["selectedClient"]),
    ...mapFields("csvs", [
      "detailItem.id",
      "detailItem.name",
      "detailItem.description",
      "detailItem.client_ids",
      "detailItem.client_id"
    ]),
    selectedComponents() {
      let comps = [];
      this.selectedIds.forEach(id => {
        comps.push(this.test_components.items.find(item => item.id === id));
      });
      return comps;
    },
    availableComponents() {
      return this.test_components.items.filter(
        item => !this.selectedIds.includes(item.id)
      );
    },
    isNew() {
      return !this.id;
    },
    pageTitle() {
      return `${this.isNew ? "Add" : "Edit"} Custom CSV Download`;
    },
    isAllClientsSelected() {
      return (
        Array.isArray(this.client_ids) &&
        this.client_ids.length === this.allUserClientIds.length &&
        this.allUserClientIds.length > 0
      );
    },
    isSomeClientsSelected() {
      return (
        Array.isArray(this.client_ids) &&
        this.client_ids.length > 0 &&
        this.client_ids.length < this.allUserClientIds.length
      );
    }
  },
  methods: {
    ...mapMutations("csvs", ["clearDetailItem"]),
    ...mapActions("csvs", ["getCsv", "addCsv", "editCsv"]),
    ...mapActions("baseData", ["getTestComponents"]),
    startDrag(evt, item) {
      evt.dataTransfer.dropEffect = "move";
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("itemId", item.id);
    },
    onNameBlur() {
      this.name = this.name?.trim() || "";
    },
    dragEnter(evt) {
      if (evt.clientY > evt.target.height / 2) {
        evt.target.style.marginBottom = "10px";
      } else {
        evt.target.style.marginTop = "10px";
      }
    },
    dragLeave(evt) {
      evt.target.style.marginTop = "2px";
      evt.target.style.marginBottom = "2px";
    },
    onDrop(evt, includeIt, idx = -1) {
      if (evt.target.style.marginTop === "10px") {
        evt.target.style.marginTop = "2px";
      }
      if (evt.target.style.marginBottom === "10px") {
        evt.target.style.marginBottom = "2px";
      }
      const id = parseInt(evt.dataTransfer.getData("itemId"));
      if (includeIt) {
        this.add(id);
        this.moveItem(this.selectedIds.indexOf(id), idx);
      } else {
        this.remove(id);
      }
      evt.stopPropagation();
    },
    add(id) {
      if (!this.selectedIds.includes(id)) {
        this.selectedIds.push(id);
      }
    },
    remove(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      }
    },
    moveItem(from, to) {
      if (to < 0) to = this.selectedIds.length;
      this.selectedIds.splice(to, 0, this.selectedIds.splice(from, 1)[0]);
    },
    compName(id) {
      return this.test_components.items.find(item => item.id === id).title;
    },
    cancel() {
      this.clearDetailItem();
      this.$router.push("/reports/custom-csvs");
    },
    loadCsv(id) {
      if (id && id !== this.id) {
        return this.getCsv(id).catch(() => {
          this.cancel();
        });
      } else {
        if (!id && this.id) {
          this.clearDetailItem();
        }
        return Promise.resolve();
      }
    },
    async saveCustomCsv() {
      if (!this.$refs.form.validate()) return;

      this.saveLoading = true;

      try {
        this.client_id = this.client_ids;

        let detailItem = {};
        Object.assign(detailItem, this.detailItem);

        detailItem.custom_csv_test_components = this.selectedIds.map(id => ({
          test_component_id: id
        }));

        let promise = this.isNew
          ? this.addCsv(detailItem)
          : this.editCsv(detailItem);

        await promise;

        this.clearDetailItem();
        this.$router.push("/reports/custom-csvs");
      } catch (e) {
        console.error(e);
      } finally {
        this.saveLoading = false;
      }
    },
    toggleSelectAll(val) {
      if (val) {
        // checked → select all
        this.client_ids = [...this.allUserClientIds];
      } else if (this.isAllClientsSelected) {
        // unchecked from checked → clear all
        this.client_ids = [];
      }
      // indeterminate → do NOTHING
    }
  },
  watch: {
    $route(to) {
      this.loadCsv(to.params.id);
    },
    selectedClient(newVal) {
      if (!this.user.is_copy_to) {
        this.client_ids = newVal ? [newVal.id] : null;
      }
    }
  },
  mounted() {
    this.clearDetailItem();
    this.loading = true;
    Promise.all([
      this.loadCsv(this.$route.params.id),
      this.getTestComponents()
    ]).then(() => {
      if (!this.isNew) {
        this.detailItem.custom_csv_test_components.forEach(item => {
          this.add(item.test_component_id);
        });
      }
      this.loading = false;
    });
    this.client_ids = this.isNew
      ? this.user.is_copy_to
        ? []
        : [this.selectedClient?.id]
      : this.client_ids ?? null;
    this.allUserClientIds = this.user?.clients.map(c => c.id);
    this.disableSelectAllClients =
      this.allUserClientIds?.length == this.client_ids?.length;
  }
};
</script>

<style scoped>
.flex-column {
  background-color: #efefef;
}
.v-list {
  background-color: #efefef;
}
.v-list-item {
  padding-top: 1px;
  background-color: #fff;
  border: solid 2px #efefef;
  border-left-width: 3px;
  border-right-width: 3px;
  border-radius: 8px;
}
.v-list-item__content {
  pointer-events: none;
}
</style>
